// components/NavigationMenu.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronDown,
  Menu as MenuIcon,
  Settings,
  Trophy,
  BookOpen,
  CheckSquare,
  Repeat,
  Calendar,
  LogOut,
  Clock,
  Book,
  Target,
  Video,
  Star,
  History,
  Shield
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import { supabase } from '../lib/supabase';

export const menuItems = [
  { icon: Target, label: 'Create New Plan', path: '/create-plan' },
  { icon: Settings, label: 'Profile Settings', path: '/profile' },
  {
    icon: Calendar,
    label: 'Schedule a Coaching Call',
    onClick: () => {
      window.open('https://link.elite360.io/widget/booking/tjH770LiUt0mSG35UKRl', '_blank');
    }
  },
  {
    icon: Video,
    label: 'Video Library',
    children: [
      { icon: Video, label: 'All Videos', path: '/dashboard/videos' },
      { icon: Star, label: 'Favorites', path: '/dashboard/videos/favorites' },
      { icon: History, label: 'Watch History', path: '/dashboard/videos/history' }
    ]
  },
  {
    icon: Clock,
    label: 'History',
    children: [
      { icon: BookOpen, label: 'Journal', path: '/journal/history' },
      { icon: Trophy, label: 'Lessons Learned', path: '/lessons-history' }
    ]
  },
  {
    icon: Book,
    label: 'Knowledge Base',
    children: [
      { icon: Trophy, label: 'How Scoring Works', path: '/scoring' },
      { icon: BookOpen, label: 'JC Growth System', path: '/journaling-overview' },
      { icon: CheckSquare, label: 'Task Overview', path: '/task-overview' },
      { icon: Repeat, label: 'Power of Repetition', path: '/repetition-power' }
    ]
  },
  {
    icon: LogOut,
    label: 'Sign Out',
    onClick: async () => {
      await supabase.auth.signOut();
      window.location.href = '/';
    }
  }
];

export function NavigationMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminStatus();
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function checkAdminStatus() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      setIsAdmin(profile?.is_admin || false);
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  }

  const toggleSection = (label: string) => {
    setOpenSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const getMenuItems = () => {
    const items = [...menuItems];

    if (isAdmin) {
      items.splice(2, 0, {
        icon: Shield,
        label: 'Video Admin Panel',
        path: '/dashboard/admin/videos'
      });
    }

    return items;
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-1 sm:px-4 sm:py-2 rounded-md border border-[#236caf] text-[#236caf] text-sm sm:text-base hover:bg-blue-50"
      >
        <MenuIcon className="w-5 h-5" />
        <ChevronDown
          className={cn(
            'w-4 h-4 transition-transform duration-200 hidden sm:inline',
            isOpen && 'transform rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg py-2 z-50">
          {getMenuItems().map((item, index) => {
            if (item.children) {
              return (
                <div key={index}>
                  <button
                    onClick={() => toggleSection(item.label)}
                    className="w-full flex justify-between items-center px-4 py-2 text-gray-700 hover:bg-blue-50"
                  >
                    <div className="flex items-center gap-3">
                      {item.icon && <item.icon className="w-4 h-4 text-[#236caf]" />}
                      <span>{item.label}</span>
                    </div>
                    <ChevronDown className={cn(
                      'w-4 h-4 transition-transform duration-200',
                      openSections[item.label] && 'transform rotate-180'
                    )} />
                  </button>
                  {openSections[item.label] && (
                    <div className="pl-6">
                      {item.children.map((subItem, subIndex) => (
                        <button
                          key={subIndex}
                          onClick={() => {
                            if (subItem.onClick) {
                              subItem.onClick();
                            } else {
                              navigate(subItem.path!);
                            }
                            setIsOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-gray-600 hover:bg-blue-50 text-sm"
                        >
                          {subItem.icon && <subItem.icon className="w-4 h-4 text-[#236caf]" />}
                          <span>{subItem.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            } else {
              return (
                <button
                  key={index}
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      navigate(item.path!);
                    }
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                >
                  {item.icon && <item.icon className="w-4 h-4 text-[#236caf]" />}
                  <span>{item.label}</span>
                </button>
              );
            }
          })}
        </div>
      )}
    </div>
  );
}
